import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useCallback } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  Pagination,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import AcreedoresApi from "api/acreedoresApi";

import ApoderadosAcreedorModal from "./apoderadosAcreedor";
import CreateAcreedor from "./createAcreedor";
import EditAcreedorModal from "./editAcreedorModal";

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AcreedoresTable = () => {
  const [acreedores, setAcreedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalApoderadosOpen, setModalApoderadosOpen] = useState(false);
  const [selectedAcreedor, setSelectedAcreedor] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRecurrent, setShowRecurrent] = useState(true);
  const [error, setError] = useState(null);

  // Estados de paginación del servidor
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    page_size: 25,
    has_next: false,
    has_previous: false,
  });

  // Debounced search term para evitar múltiples calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Función optimizada para obtener acreedores con paginación del servidor
  const fetchAcreedores = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        setError(null);

        const requestParams = {
          page: params.page || pagination.current_page || 1,
          page_size: params.page_size || pagination.page_size || 25,
          search: debouncedSearchTerm,
          es_recurrente: showRecurrent,
          ...params,
        };

        const response = await AcreedoresApi.fetchAcreedoresPaginacion(requestParams);

        setAcreedores(response.data.results);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error("Error al obtener acreedores:", error);
        setError(
          obtenerMensajeError(error, "Error al cargar los acreedores. Por favor, intente de nuevo.")
        );
        setAcreedores([]);
        setPagination((prev) => ({ ...prev, total_items: 0, total_pages: 0 }));
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearchTerm, showRecurrent]
  );

  // Efecto para cargar datos cuando cambian los filtros
  useEffect(() => {
    setPagination((prev) => ({ ...(prev || {}), current_page: 1 }));
    fetchAcreedores({ page: 1 });
  }, [debouncedSearchTerm, showRecurrent]);

  // Efecto para carga inicial
  useEffect(() => {
    fetchAcreedores();
  }, []);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let acreedor = acreedores.find((a) => a.id === id);
    setSelectedAcreedor(acreedor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleApoderados = (acreedor) => {
    setSelectedAcreedor(acreedor);
    setModalApoderadosOpen(true);
    handleMenuClose();
  };

  const handleEdit = (acreedor) => {
    setSelectedAcreedor(acreedor);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar este acreedor?")) {
      try {
        setLoading(true);
        await AcreedoresApi.deleteAcreedor(id);

        // Recargar la página actual después de eliminar
        await fetchAcreedores();
      } catch (error) {
        console.error("Error al eliminar acreedor:", error);
        setError(obtenerMensajeError(error, "Error al eliminar el acreedor."));
      } finally {
        setLoading(false);
      }
    }
    handleMenuClose();
  };

  const handleRecurrentChange = (event) => {
    setShowRecurrent(event.target.checked);
    // El useEffect se encargará de hacer el fetch con page: 1
  };

  // Función para cambiar de página
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, current_page: newPage }));
    fetchAcreedores({ page: newPage });
  };

  // Función para cambiar el tamaño de página (si usas TableTopControls)
  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      page_size: newPageSize,
      current_page: 1,
    }));
    fetchAcreedores({ page: 1, page_size: newPageSize });
  };

  // Función para refrescar
  const handleRefresh = () => {
    fetchAcreedores();
  };

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuClick(e, params.row.id)}
          sx={{ padding: "4px" }}
          disabled={loading}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "nombre",
      headerName: "Nombre",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre_completo}
        </SuiTypography>
      ),
      sx: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    {
      field: "tipo",
      headerName: "Tipo",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption">{params.row.tipo || "N/A"}</SuiTypography>
      ),
      sx: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    {
      field: "identificacion",
      headerName: "NIT/CC",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption">{params.row.identificacion}</SuiTypography>
      ),
      sx: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
  ];

  return (
    <SuiBox>
      {/* Loading backdrop solo para carga inicial */}
      {initialLoading && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
              Cargando...
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Administración de Acreedores ({pagination?.total_items || 0})</strong>
                {/* <SuiTypography variant="caption" color="text">
                  {pagination?.total_items || 0} acreedores encontrados
                </SuiTypography> */}
              </SuiBox>
            </Grid>
          </Grid>

          <SuiBox display="flex" alignItems="center" gap={1} sx={{ ml: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showRecurrent}
                  onChange={handleRecurrentChange}
                  color="primary"
                  disabled={loading}
                />
              }
              label={
                <SuiTypography variant="button" fontWeight="regular">
                  Recurrentes
                </SuiTypography>
              }
            />

            <Tooltip title="Buscar por Nombre, Apellido e Identificación" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar acreedores..."
                disabled={loading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>

            {/* Mantener TableTopControls si lo usas para cambiar page size */}
            <TableTopControls
              page={(pagination?.current_page || 1) - 1} // TableTopControls usa 0-based
              onPageChange={(newPage) => handlePageChange(null, newPage + 1)} // Convertir a 1-based
              pageSize={pagination?.page_size || 25}
              onPageSizeChange={handlePageSizeChange}
              totalRows={pagination?.total_items || 0}
            />

            <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ ml: 1 }}>
              <RefreshIcon />
            </IconButton>

            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedAcreedor(null);
                setShowCreateForm(true);
              }}
              disabled={loading}
            >
              Nuevo Acreedor
            </SuiButton>
          </SuiBox>
        </SuiBox>

        {/* Mostrar error si existe */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={acreedores}
          columns={columns}
          loading={loading && !initialLoading}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 1,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[200],
              },
              minHeight: "24px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "24px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />

        {/* Paginación personalizada */}
        {(pagination?.total_pages || 0) > 1 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            sx={{
              borderTop: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            }}
          >
            <Box>
              <SuiTypography variant="caption" color="text">
                Página {pagination?.current_page || 1} de {pagination?.total_pages || 1}(
                {pagination?.total_items || 0} registros totales)
              </SuiTypography>
            </Box>

            <Pagination
              count={pagination?.total_pages || 1}
              page={pagination?.current_page || 1}
              onChange={handlePageChange}
              disabled={loading}
              color="primary"
              showFirstButton
              showLastButton
              size="small"
            />
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleApoderados(acreedores.find((a) => a.id === selectedId))}>
          <AddCircleOutlineIcon title="APODERADOS" fontSize="small" sx={{ mr: 1 }} /> Apoderados
        </MenuItem>
        <MenuItem onClick={() => handleEdit(acreedores.find((a) => a.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedId)} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      <ApoderadosAcreedorModal
        open={modalApoderadosOpen}
        onClose={() => setModalApoderadosOpen(false)}
        acreedor={selectedAcreedor}
        onSuccess={() => {
          fetchAcreedores();
          //setModalApoderadosOpen(false);
        }}
      />
      <CreateAcreedor
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSuccess={() => {
          fetchAcreedores();
          setShowCreateForm(false);
        }}
      />
      <EditAcreedorModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        acreedor={selectedAcreedor}
        onSuccess={() => {
          fetchAcreedores();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default AcreedoresTable;
